import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';

/**
 * One number per case (proposal 6, kanban c98164ed). Every tester asked why a
 * case carries QT-, INV- and sometimes LEG- numbers and which one to quote.
 * The rule, in one place: a case shows its INV- number everywhere once it
 * exists; a thread with no case shows its QT- number. The other references
 * sit in one "Also known as" line under the main identifier on the header.
 */

/** One entry of the "Also known as" line: a lead-in and the reference it
 *  names (null when there is none yet, e.g. no sales-site complaint). */
export interface AlsoKnownAs {
  kind: 'thread' | 'complaint' | 'quote';
  /** Words before the reference ("Merlin thread", "quote"). */
  lead: string;
  /** The reference itself, or null for "none filed yet". */
  ref: string | null;
  /** Words after the reference ("to colleagues"), or the stand-in when
   *  `ref` is null. */
  tail: string;
}

export interface CaseIdentity {
  /** The one identifier to show: INV-… once a case exists, else QT-…. */
  label: string;
  kind: 'case' | 'thread';
  /** Empty for a thread with no case: there is nothing else to call it. */
  alsoKnownAs: AlsoKnownAs[];
}

type ThreadRef = Pick<DoorThreadRead, 'thread_no'>;
type CaseRef = Pick<DoorInvestigationRead, 'investigation_no'> &
  Partial<Pick<DoorInvestigationRead, 'linked_issues'>>;

/** The identifier a thread and/or its case go by (see module comment). */
export function primaryRef(thread: ThreadRef | null, investigation: CaseRef | null): CaseIdentity {
  if (!investigation) {
    return { label: thread?.thread_no ?? '', kind: 'thread', alsoKnownAs: [] };
  }
  const no = investigation.investigation_no;
  const aka: AlsoKnownAs[] = [];
  if (thread) aka.push({ kind: 'thread', lead: 'Merlin thread', ref: thread.thread_no, tail: '' });
  const legs = [...new Set((investigation.linked_issues ?? []).map((i) => i.issue_no))];
  aka.push(
    legs.length > 0
      ? { kind: 'complaint', lead: 'sales-site complaint', ref: legs.join(', '), tail: '' }
      : { kind: 'complaint', lead: 'sales-site complaint:', ref: null, tail: 'none filed yet' },
  );
  aka.push({ kind: 'quote', lead: 'quote', ref: no, tail: 'to colleagues' });
  return { label: no, kind: 'case', alsoKnownAs: aka };
}

/** The "Also known as" line as plain text (titles, aria labels, tests). */
export function alsoKnownAsText(identity: CaseIdentity): string {
  if (identity.alsoKnownAs.length === 0) return '';
  const parts = identity.alsoKnownAs.map((a) =>
    [a.lead, a.ref, a.tail].filter((s) => !!s).join(' '),
  );
  return `Also known as: ${parts.join(' · ')}`;
}
